import type { RequestFn } from '../http';

export type UnknownCallerStatus = 'PENDING' | 'ASSIGNED' | 'IGNORED' | 'COLD_CALLER';

export interface UnknownCallerListItem {
  id: string;
  phoneE164: string;
  phoneRaw: string | null;
  firstSeenAt: string;
  lastSeenAt: string;
  callCount: number;
  status: UnknownCallerStatus;
  lastSummary: string | null;
  lastConversationId: string | null;
  assignedCustomerId: string | null;
  assignedCustomer: { id: string; name: string } | null;
  assignedContactId: string | null;
  assignedContact: { id: string; name: string; firstName: string | null; lastName: string | null; jobRole: string | null } | null;
  notes: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface UnknownCallersListResponse {
  items: UnknownCallerListItem[];
  nextCursor: string | null;
  /** Echte Gesamtanzahl passender Einträge (unabhängig vom Page-Limit). */
  total?: number;
}

export interface UnknownCallersListParams {
  status?: UnknownCallerStatus | '';
  from?: string;
  to?: string;
  minCalls?: number;
  q?: string;
  cursor?: string;
  limit?: number;
}

export interface AssignCustomerPayload {
  customerId: string;
  contact?: {
    name: string;
    firstName?: string;
    lastName?: string;
    salutation?: string;
    jobRole?: string;
    email?: string;
  };
}

export interface CreateCustomerPayload {
  name: string;
  branch?: string;
  customerType?: 'CUSTOMER' | 'SALES_LEAD' | 'SUPPLIER' | 'PARTNER';
  contact?: {
    name: string;
    firstName?: string;
    lastName?: string;
    salutation?: string;
    jobRole?: string;
    email?: string;
  };
}

export interface AssignSupplierPartnerPayload {
  customerId?: string;
  name?: string;
  contact?: {
    name: string;
    firstName?: string;
    lastName?: string;
    salutation?: string;
    jobRole?: string;
    email?: string;
  };
}

export interface CreateContactPayload {
  name?: string;
  firstName?: string;
  lastName?: string;
  salutation?: string;
  jobRole?: string;
  email?: string;
  company?: string;
  notes?: string;
}

export function createUnknownCallersApi(request: RequestFn) {
  return {
    async listUnknownCallers(params: UnknownCallersListParams = {}) {
      const qs = new URLSearchParams();
      if (params.status) qs.set('status', params.status);
      if (params.from) qs.set('from', params.from);
      if (params.to) qs.set('to', params.to);
      if (params.minCalls) qs.set('minCalls', String(params.minCalls));
      if (params.q) qs.set('q', params.q);
      if (params.cursor) qs.set('cursor', params.cursor);
      if (params.limit) qs.set('limit', String(params.limit));
      const query = qs.toString();
      return request<UnknownCallersListResponse>(
        `/admin/unknown-callers${query ? `?${query}` : ''}`,
      );
    },

    async getUnknownCaller(id: string) {
      return request<{ item: UnknownCallerListItem }>(`/admin/unknown-callers/${id}`);
    },

    async assignCustomer(id: string, payload: AssignCustomerPayload) {
      return request<{ item: UnknownCallerListItem }>(
        `/admin/unknown-callers/${id}/assign-customer`,
        { method: 'POST', body: JSON.stringify(payload) },
      );
    },

    async createCustomer(id: string, payload: CreateCustomerPayload) {
      return request<{ item: UnknownCallerListItem; customer: { id: string; name: string } }>(
        `/admin/unknown-callers/${id}/create-customer`,
        { method: 'POST', body: JSON.stringify(payload) },
      );
    },

    async assignSupplier(id: string, payload: AssignSupplierPartnerPayload) {
      return request<{ item: UnknownCallerListItem }>(
        `/admin/unknown-callers/${id}/assign-supplier`,
        { method: 'POST', body: JSON.stringify(payload) },
      );
    },

    async assignPartner(id: string, payload: AssignSupplierPartnerPayload) {
      return request<{ item: UnknownCallerListItem }>(
        `/admin/unknown-callers/${id}/assign-partner`,
        { method: 'POST', body: JSON.stringify(payload) },
      );
    },

    async createContact(id: string, payload: CreateContactPayload) {
      return request<{ item: UnknownCallerListItem; contact: { id: string; name: string } }>(
        `/admin/unknown-callers/${id}/create-contact`,
        { method: 'POST', body: JSON.stringify(payload) },
      );
    },

    async markColdCaller(id: string, notes?: string) {
      return request<{ item: UnknownCallerListItem }>(
        `/admin/unknown-callers/${id}/mark-cold-caller`,
        { method: 'POST', body: JSON.stringify({ notes }) },
      );
    },

    async ignore(id: string, notes?: string) {
      return request<{ item: UnknownCallerListItem }>(
        `/admin/unknown-callers/${id}/ignore`,
        { method: 'POST', body: JSON.stringify({ notes }) },
      );
    },
  };
}
